import React, { useEffect, useState } from 'react';
import { Menu, message } from 'antd';
import {
  LeftOutlined, LogoutOutlined, UserOutlined, TagsOutlined, SlidersOutlined,
  FileTextOutlined, StarOutlined, FormOutlined, CommentOutlined, HistoryOutlined,
  FlagOutlined, SafetyCertificateOutlined, MessageOutlined, BellOutlined,
  TeamOutlined, AuditOutlined, BarChartOutlined, FileSearchOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLogout } from '@/apis/user';
import { selectIsAdmin } from '@/store/modules/user';
import { userRole } from '@/utils/roles';
import './index.scss';

const navigationGroups = [
  { key: 'profile', label: '个人与偏好', paths: ['/personal/info', '/personal/tags', '/personal/feedback', '/personal/recommendation-history'] },
  { key: 'activity', label: '帖子与活动', paths: ['/personal/myposts', '/personal/favorites', '/personal/applications', '/personal/reviews'] },
  { key: 'settings', label: '账号与通知', paths: ['/personal/security', '/personal/chat-settings', '/personal/notification-settings', '/personal/reports'] },
  { key: 'management', label: '管理与统计', paths: ['/personal/admin', '/personal/report-management', '/personal/statistics', '/personal/audit'] },
];

// 桌面侧栏与移动端个人中心复用相同的权限和退出逻辑。
export const usePersonalNavigationItems = ({ showParentReturn = false } = {}) => {
  const { pathname: routePath } = useLocation();
  const pathname = routePath.replace(/\/+$/, '') || '/';
  const returnLink = ['/personal/email', '/personal/modify'].includes(pathname)
    ? { path: '/personal/info', label: '返回个人资料' }
    : /^\/personal\/applications\/[^/]+$/.test(pathname)
      ? { path: '/personal/applications', label: '返回我的申请' }
      : null;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAdmin = useSelector(selectIsAdmin);
  const superAdmin = useSelector(state => userRole(state.user.userInfo) === 'super_admin');
  const handleLogout = async () => {
    navigate('/', { replace: true });
    try {
      const result = await dispatch(fetchLogout());
      if (result.success) message.success('退出登录成功');
      else message.error(`${result.error || '服务器退出失败'}；本地已退出，刷新页面可能恢复登录`);
    } catch (error) { message.error('退出登录失败，请稍后重试'); }
  };
  const items = [
    { key: '/personal/info', icon: <UserOutlined />, label: <Link to="/personal/info">个人资料</Link> },
    { key: '/personal/myposts', icon: <FileTextOutlined />, label: <Link to="/personal/myposts">我的帖子</Link> },
    { key: '/personal/favorites', icon: <StarOutlined />, label: <Link to="/personal/favorites">我的收藏</Link> },
    { key: '/personal/applications', icon: <FormOutlined />, label: <Link to="/personal/applications">我的申请</Link> },
    { key: '/personal/reviews', icon: <CommentOutlined />, label: <Link to="/personal/reviews">我的评价</Link> },
    { key: '/personal/feedback', icon: <SlidersOutlined />, label: <Link to="/personal/feedback">推荐偏好</Link> },
    { key: '/personal/recommendation-history', icon: <HistoryOutlined />, label: <Link to="/personal/recommendation-history">推荐记录</Link> },
    { key: '/personal/reports', icon: <FlagOutlined />, label: <Link to="/personal/reports">我的举报</Link> },
    { key: '/personal/security', icon: <SafetyCertificateOutlined />, label: <Link to="/personal/security">登录会话</Link> },
    { key: '/personal/chat-settings', icon: <MessageOutlined />, label: <Link to="/personal/chat-settings">聊天设置</Link> },
    { key: '/personal/notification-settings', icon: <BellOutlined />, label: <Link to="/personal/notification-settings">通知设置</Link> },
    { key: '/personal/tags', icon: <TagsOutlined />, label: <Link to="/personal/tags">兴趣标签</Link> },
    ...(isAdmin ? [{ key: '/personal/admin', icon: <TeamOutlined />, label: <Link to="/personal/admin">管理入口</Link> }] : []),
    ...(isAdmin ? [{ key: '/personal/report-management', icon: <AuditOutlined />, label: <Link to="/personal/report-management">举报处理</Link> }] : []),
    ...(isAdmin ? [{ key: '/personal/statistics', icon: <BarChartOutlined />, label: <Link to="/personal/statistics">统计概览</Link> }] : []),
    ...(isAdmin && superAdmin ? [{ key: '/personal/audit', icon: <FileSearchOutlined />, label: <Link to="/personal/audit">审计日志</Link> }] : []),
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true, onClick: handleLogout },
  ];
  const links = items.map(item => returnLink && showParentReturn && item.key === returnLink.path
    ? { ...item, icon: <LeftOutlined />, label: <Link to={returnLink.path}>{returnLink.label}</Link> }
    : item);
  const groups = navigationGroups.map(group => ({
    key: group.key,
    label: group.label,
    children: group.paths.map(path => links.find(item => item.key === path)).filter(Boolean),
  })).filter(group => group.children.length > 0);
  const menuItems = [...groups, ...links.filter(item => item.type === 'divider' || item.key === 'logout')];
  const selectedKeys = items.some(item => item.key === pathname) ? [pathname] : [];
  const parentPath = returnLink?.path || pathname;
  const activeGroup = groups.find(group => group.children.some(item => parentPath === item.key || parentPath.startsWith(`${item.key}/`)))?.key || 'profile';
  return { items: menuItems, selectedKeys, activeGroup, pathname, groupKeys: groups.map(group => group.key) };
};

const PersonalNavigation = ({ onNavigate, showParentReturn = false }) => {
  const { items: menuItems, selectedKeys, activeGroup, pathname, groupKeys } = usePersonalNavigationItems({ showParentReturn });
  const [openKeys, setOpenKeys] = useState([activeGroup]);
  const groupKeySignature = groupKeys.join(',');
  useEffect(() => {
    const availableKeys = groupKeySignature.split(',');
    setOpenKeys(previous => [...new Set([...previous.filter(key => availableKeys.includes(key)), activeGroup])]);
  }, [activeGroup, pathname, groupKeySignature]);
  return <Menu className="personal-navigation-menu" aria-label="个人中心导航" mode="inline" inlineIndent={16}
    selectedKeys={selectedKeys} openKeys={openKeys} onOpenChange={setOpenKeys} items={menuItems} onClick={onNavigate} />;
};

export default PersonalNavigation;
